/**
 * Web Push for the browser consoles (admin, merchant).
 *
 * The backend has had the sending half for a long time — VAPID keys in ``platform_settings``, a
 * ``push_subscriptions`` collection, ``notify._send_webpush`` — but nothing in the app ever registered a service
 * worker or called ``POST /push/web/subscribe``, so that collection was always empty and no browser notification
 * could ever be delivered. This is the missing half.
 *
 * Everything here degrades quietly: no service worker support, no VAPID keys configured, permission denied, or an
 * offline subscribe all leave the console working exactly as before (the in-page alert feed), never broken.
 */
import { api } from "./api";

export const SW_URL = "/bizil-push-sw.js";
export const WEB_PUSH_MESSAGE = "bizil:web-push";

/** ``supported`` is about the browser; everything else is state we can act on. */
export function webPushSupported() {
    return typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
}

/** VAPID public keys are base64url; the subscribe API wants raw bytes. */
export function urlBase64ToUint8Array(base64) {
    const padded = String(base64 || "").replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

async function vapidKey() {
    try {
        const { data } = await api.get("/push/vapid-public-key");
        return data?.public_key || null;
    } catch {
        // 503 = the platform has no VAPID keys yet (Super Admin → Firebase → Web Push). Not an error here.
        return null;
    }
}

export async function getWebPushState() {
    if (!webPushSupported()) return { supported: false, state: "unsupported" };
    return { supported: true, state: Notification.permission };   // "granted" | "denied" | "default"
}

/**
 * Register the worker and subscribe this browser, sending the subscription to the backend.
 *
 * ``prompt: false`` (the default) never shows the browser's permission dialog — it only completes a subscription
 * for a browser that already granted it, which is what we want to run automatically on every console load.
 * ``prompt: true`` is for an explicit click, because Chrome and Safari both ignore a permission request that
 * isn't tied to a user gesture.
 */
export async function ensureWebPushSubscription({ prompt = false } = {}) {
    if (!webPushSupported()) return { state: "unsupported" };
    if (Notification.permission === "denied") return { state: "denied" };
    if (Notification.permission !== "granted") {
        if (!prompt) return { state: "default" };
        let asked = "default";
        try { asked = await Notification.requestPermission(); } catch { return { state: "error" }; }
        if (asked !== "granted") return { state: asked };
    }

    const key = await vapidKey();
    if (!key) return { state: "unconfigured" };

    try {
        const reg = await navigator.serviceWorker.register(SW_URL);
        await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (sub) {
            // The platform's VAPID key can be rotated; a subscription minted under the old one is dead on arrival.
            const current = new Uint8Array(sub.options?.applicationServerKey || []);
            const wanted = urlBase64ToUint8Array(key);
            const sameKey = current.length === wanted.length && current.every((b, i) => b === wanted[i]);
            if (!sameKey) {
                try { await sub.unsubscribe(); } catch { /* best effort */ }
                sub = null;
            }
        }
        if (!sub) {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(key),
            });
        }
        await api.post("/push/web/subscribe", {
            subscription: JSON.parse(JSON.stringify(sub)),
            user_agent: navigator.userAgent,
        });
        return { state: "granted", endpoint: sub.endpoint };
    } catch (e) {
        // Blocked by an extension, an insecure origin, a rejected registration — the console still works.
        console.warn("[webPush] subscribe failed:", e?.message || e);
        return { state: "error", error: e?.message || String(e) };
    }
}

/** Drop this browser's subscription. Called on logout so the next person on a shared machine isn't pinged. */
export async function removeWebPushSubscription() {
    if (!webPushSupported()) return;
    try {
        const reg = await navigator.serviceWorker.getRegistration(SW_URL);
        const sub = reg && (await reg.pushManager.getSubscription());
        if (!sub) return;
        // Short timeout on purpose: this runs inside logout, ahead of the logout call itself (the row has to be
        // dropped while the session is still valid). api's default is 20 s, and two sequential 20 s waits would
        // double how long a logout hangs on a dead connection. The local unsubscribe below is what actually stops
        // this browser receiving, and it runs either way.
        try {
            await api.delete("/push/web/subscribe", { data: { endpoint: sub.endpoint }, timeout: 5000 });
        } catch { /* offline, or the session already ended — the row is dropped on its next failed delivery */ }
        try { await sub.unsubscribe(); } catch { /* already gone */ }
    } catch { /* nothing registered */ }
}

/** Listen for pushes the worker forwards to this tab. Returns an unsubscribe function. */
export function onWebPushMessage(handler) {
    if (!webPushSupported()) return () => {};
    const listener = (event) => {
        const msg = event?.data;
        if (msg?.type === WEB_PUSH_MESSAGE) handler(msg.payload || {});
    };
    navigator.serviceWorker.addEventListener("message", listener);
    return () => navigator.serviceWorker.removeEventListener("message", listener);
}
